import { supabase } from '@/integrations/supabase/client';
import { MarketplaceProduct, MarketplaceBrand } from '@/types/marketplace/marketplace';

export interface GenerateImageParams {
  product_id?: string;
  brand_name?: string;
  product_name: string;
  model_number?: string;
  category_slug?: string;
  description?: string;
  custom_prompt?: string;
  aspect_ratio?: string;
  model?: 'nano-banana' | 'imagen-4' | 'imagen-4-fast';
}

export interface GenerateImageResponse {
  success: boolean;
  image_url: string;
  replicate_prediction_id: string;
  storage_path: string;
}

export class ProductImageService {
  /**
   * Generate a product image using Replicate AI
   */
  static async generateProductImage(params: GenerateImageParams): Promise<GenerateImageResponse> {
    const { data, error } = await supabase.functions.invoke('generate-product-image', {
      body: params
    });

    if (error) {
      console.error('Error generating product image:', error);
      throw new Error(`Failed to generate image: ${error.message}`);
    }

    if (!data.success) {
      throw new Error(data.error || 'Image generation failed');
    }

    return data;
  }

  /**
   * Generate image for an existing product
   */
  static async generateImageForProduct(
    product: MarketplaceProduct,
    brand?: MarketplaceBrand,
    categorySlug?: string
  ): Promise<string> {
    const params: GenerateImageParams = {
      product_id: product.id,
      product_name: product.name,
      model_number: product.model_number || undefined,
      brand_name: brand?.name,
      category_slug: categorySlug,
      description: product.description || undefined
    };

    const result = await this.generateProductImage(params);
    return result.image_url;
  }

  /**
   * Generate images for multiple products in batch
   */
  static async generateImagesForProducts(
    products: Array<{
      product: MarketplaceProduct;
      brand?: MarketplaceBrand;
      categorySlug?: string;
    }>,
    onProgress?: (completed: number, total: number, currentProduct: string) => void
  ): Promise<Array<{ product_id: string; image_url: string; error?: string }>> {
    const results: Array<{ product_id: string; image_url: string; error?: string }> = [];

    for (let i = 0; i < products.length; i++) {
      const { product, brand, categorySlug } = products[i];

      try {
        if (onProgress) {
          onProgress(i, products.length, product.name);
        }

        const imageUrl = await this.generateImageForProduct(product, brand, categorySlug);
        results.push({
          product_id: product.id,
          image_url: imageUrl
        });

        // Wait 2 seconds between requests to avoid rate limiting
        if (i < products.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      } catch (error) {
        console.error(`Failed to generate image for ${product.name}:`, error);
        results.push({
          product_id: product.id,
          image_url: '',
          error: error instanceof Error ? error.message : 'Unknown error'
        });
      }
    }

    if (onProgress) {
      onProgress(products.length, products.length, 'Complete');
    }

    return results;
  }

  /**
   * Generate a custom product image with specific prompt
   */
  static async generateCustomImage(
    productName: string,
    customPrompt: string,
    productId?: string
  ): Promise<string> {
    const params: GenerateImageParams = {
      product_id: productId,
      product_name: productName,
      custom_prompt: customPrompt
    };

    const result = await this.generateProductImage(params);
    return result.image_url;
  }

  /**
   * Build a custom prompt with Help Genie branding
   */
  static buildBrandedPrompt(
    productName: string,
    brandName?: string,
    modelNumber?: string,
    categoryContext?: string
  ): string {
    const parts = [];

    if (brandName) {
      parts.push(brandName);
    }
    parts.push(productName);
    if (modelNumber) {
      parts.push(`model ${modelNumber}`);
    }

    const productDesc = parts.join(' ');

    const context = categoryContext || 'clean product display, professional setting';

    const baseStyle = `
      Professional studio product photography,
      clean white background with subtle purple and blue accent lighting,
      3/4 view angle, soft shadows, premium aesthetic,
      high-key lighting, shallow depth of field,
      make the scene natural, photorealistic, 4K quality
    `.trim().replace(/\s+/g, ' ');

    return `${productDesc}, ${context}, ${baseStyle}`;
  }

  /**
   * Get category-specific photography context
   */
  static getCategoryContext(categorySlug?: string): string {
    const contexts: Record<string, string> = {
      'automotive': 'luxury vehicle showroom style, reflective floor',
      'home-appliances': 'modern kitchen setting, stainless steel accent',
      'electronics': 'tech product launch style, floating on surface',
      'hvac-climate': 'home interior context, wall-mounted or floor placement',
      'solar-energy': 'roof installation context or clean tech display',
      'furniture': 'lifestyle setting, room context, natural light',
      'outdoor-lawn': 'outdoor setting, grass or patio context',
      'home-systems': 'technical diagram overlay, smart home aesthetic'
    };

    return contexts[categorySlug || ''] || 'clean product display, professional setting';
  }

  /**
   * Delete an image from storage
   */
  static async deleteImage(storagePath: string): Promise<void> {
    const { error } = await supabase.storage
      .from('marketplace-images')
      .remove([storagePath]);

    if (error) {
      console.error('Error deleting image:', error);
      throw error;
    }
  }

  /**
   * Upload an existing image file to storage
   */
  static async uploadImage(
    file: File,
    productId: string
  ): Promise<string> {
    const fileExt = file.name.split('.').pop();
    const fileName = `${productId}-${Date.now()}.${fileExt}`;
    const filePath = `products/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('marketplace-images')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) {
      throw new Error(`Failed to upload image: ${uploadError.message}`);
    }

    const { data: { publicUrl } } = supabase.storage
      .from('marketplace-images')
      .getPublicUrl(filePath);

    return publicUrl;
  }

  /**
   * Replace product image with a new one
   */
  static async replaceProductImage(
    productId: string,
    oldImageUrl: string | null,
    file?: File,
    generateNew?: boolean,
    product?: MarketplaceProduct,
    brand?: MarketplaceBrand,
    categorySlug?: string
  ): Promise<string> {
    // Delete old image if it exists and is in our storage
    if (oldImageUrl && oldImageUrl.includes('marketplace-images')) {
      try {
        const pathMatch = oldImageUrl.match(/marketplace-images\/(.+)$/);
        if (pathMatch) {
          await this.deleteImage(pathMatch[1]);
        }
      } catch (error) {
        console.warn('Failed to delete old image:', error);
        // Continue anyway
      }
    }

    // Upload new file or generate new image
    if (file) {
      return await this.uploadImage(file, productId);
    } else if (generateNew && product) {
      return await this.generateImageForProduct(product, brand, categorySlug);
    }

    throw new Error('Must provide either file or generateNew with product details');
  }
}
